import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { ArrowLeft, Clock, Download, ExternalLink, Hash, Link2, Loader2, Mail, MoreHorizontal, Paperclip, RefreshCw, Timer } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { EmlViewer } from '@/components/EmlViewer';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { InlineEdit } from '@/components/InlineEdit';
import { AssignedResourcePicker } from '@/components/AssignedResourcePicker';
import { TicketAssistant } from '@/components/TicketAssistant';
import { M365UserCard } from '@/components/cipp/M365UserCard';
import { ITGlueTicketCard } from '@/components/docs/ITGlueCards';
import { HuntressTicketCard } from '@/components/soc/HuntressCards';
import { ContactPicker } from '@/components/ContactPicker';
import { AdditionalContactsPicker } from '@/components/AdditionalContactsPicker';
import { SecondaryResourcesPicker } from '@/components/SecondaryResourcesPicker';
import { NoteForm } from '@/components/NoteForm';
import { LogTimeDialog } from '@/components/LogTimeDialog';
import { CompanyMark } from '@/components/CompanyMark';
import { UserAvatar } from '@/components/UserAvatar';
import { StatusBadge } from '@/components/StatusBadge';
import { PriorityTag } from '@/components/PriorityTag';
import { useCompanyContacts, useCompanyLookup, useContactLookup, usePicklists, useResources, useResourceRoles, useTicket, useTicketHistory } from '@/api/hooks';
import { useSession } from '@/api/auth';
import { renderWithCidImages } from '@/lib/render-cid-images';
import { dueState, fmtRelative, statusKind } from '@/lib/cobalt-helpers';
import { cn } from '@/lib/utils';
import type { TicketNote, TimeEntry } from '@/api/client';
import { ROUTES } from '@/lib/routes';
import { OrbitLink } from '@/components/insights/OrbitLink';

type NoteEntry =
  | { kind: 'ticket'; note: TicketNote }
  | { kind: 'time-summary'; entry: TimeEntry }
  | { kind: 'time-internal'; entry: TimeEntry };

const AUTOTASK_TICKET_URL = 'https://ww31.autotask.net/Mvc/ServiceDesk/TicketDetail.mvc?ticketId=';

export default function TicketDetailPage() {
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // Go back to wherever the user came from (Queues, Search, My Tickets…). When there
  // is no prior in-app history — a deep link, the notification dropdown, or a fresh
  // load — location.key is 'default', so fall back to My Tickets.
  const goBack = () =>
    location.key === 'default' ? navigate(ROUTES.psa.tickets.mine) : navigate(-1);
  const id = Number.parseInt(params.id ?? '', 10);
  const detail    = useTicket(Number.isFinite(id) ? id : undefined);
  const picklists = usePicklists();
  const resources = useResources();

  const [hideWorkflow, setHideWorkflow] = useState<boolean>(() =>
    localStorage.getItem('cosmos:notes:hide-workflow') !== 'false'
  );

  // Timer: record when this ticket was opened.
  const openedAt = useRef(new Date()).current;
  const [elapsedSecs, setElapsedSecs] = useState(0);
  const [logTimeOpen, setLogTimeOpen] = useState(false);
  useEffect(() => {
    const id = setInterval(() => setElapsedSecs(Math.floor((Date.now() - openedAt.getTime()) / 1000)), 1000);
    return () => clearInterval(id);
  }, [openedAt]);

  const ticketEarly = (detail.data?.ticket as any)?.item;
  const companies   = useCompanyLookup(ticketEarly?.companyID != null ? [ticketEarly.companyID] : []);
  const assigneeRoles = useResourceRoles(ticketEarly?.assignedResourceID ?? null);

  const noteContactIds = (detail.data?.notes.items ?? [])
    .map((n) => n.createdByContactID)
    .filter((v): v is number => v != null);
  const contacts = useContactLookup(noteContactIds);
  const history = useTicketHistory(Number.isFinite(id) ? id : undefined);
  // The ticket's own contact, for note-template placeholders. Same query key the
  // ContactPicker uses, so this is served from cache rather than a second fetch.
  const companyContacts = useCompanyContacts(ticketEarly?.companyID ?? null);
  const { data: session } = useSession();

  if (!Number.isFinite(id)) {
    return <div className="container py-8 text-sm text-destructive">Invalid ticket id.</div>;
  }

  if (detail.isLoading) {
    return (
      <div className="container flex items-center gap-2 py-8 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin"/> Loading ticket #{id}…
      </div>
    );
  }

  if (detail.error) {
    return (
      <div className="container py-8 text-sm text-destructive">
        Failed to load ticket: {detail.error instanceof Error ? detail.error.message : 'Unknown error'}
      </div>
    );
  }

  const ticket = (detail.data?.ticket as any)?.item;
  if (!ticket) return <div className="container py-8 text-sm">Ticket not found.</div>;

  const statusOpts        = picklists.data?.status ?? [];
  const priorityOpts      = picklists.data?.priority ?? [];
  const queueOpts         = picklists.data?.queueID ?? [];
  const issueTypeOpts     = picklists.data?.issueType ?? [];
  const subIssueTypeOpts  = picklists.data?.subIssueType ?? [];

  const companyName  = companies.data?.items.find((c) => c.id === ticket.companyID)?.companyName;

  // Values a note template's {{placeholders}} are filled from when one is inserted.
  const ticketContact = companyContacts.data?.items.find((c) => c.id === ticket.contactID);
  const myName = (session?.user as { name?: string } | undefined)?.name ?? '';
  const templateContext = {
    contactFirstName: ticketContact?.firstName ?? null,
    contactLastName: ticketContact?.lastName ?? null,
    contactEmail: ticketContact?.emailAddress ?? null,
    companyName: companyName ?? null,
    ticketId: ticket.id ?? id,
    ticketNumber: ticket.ticketNumber ?? null,
    ticketTitle: ticket.title ?? null,
    myFirstName: myName.split(' ')[0] || null,
    myFullName: myName || null,
  };
  const statusLabel  = ticket.status   != null ? statusOpts.find((o) => Number(o.value) === ticket.status)?.label   ?? null : null;
  const priorityLabel= ticket.priority != null ? priorityOpts.find((o) => Number(o.value) === ticket.priority)?.label ?? null : null;
  const queueLabel   = ticket.queueID  != null ? queueOpts.find((o) => Number(o.value) === ticket.queueID)?.label   ?? null : null;
  const assigneeName = ticket.assignedResourceID != null
    ? resources.data?.items.find((r) => r.id === ticket.assignedResourceID)
    : null;
  const assigneeFullName = assigneeName ? `${assigneeName.firstName} ${assigneeName.lastName}`.trim() : null;
  const assigneeRoleName = ticket.assignedResourceRoleID != null
    ? assigneeRoles.data?.items.find((r) => r.id === ticket.assignedResourceRoleID)?.name ?? null
    : null;

  const notes       = detail.data?.notes.items ?? [];
  const time        = detail.data?.time.items ?? [];
  const attachments = detail.data?.attachments.items ?? [];
  const originalEmail = attachments.find(isEmailAttachment);

  const resourceMap = new Map<number, string>();
  for (const r of resources.data?.items ?? []) {
    resourceMap.set(r.id, `${r.firstName} ${r.lastName}`.trim());
  }

  const contactMap = new Map<number, string>();
  for (const c of contacts.data?.items ?? []) {
    contactMap.set(c.id, `${c.firstName} ${c.lastName}`.trim());
  }

  // Unified notes list: ticket notes + time entry summary/internal notes, sorted newest-first
  const allNoteEntries: NoteEntry[] = [
    ...notes.map((n): NoteEntry => ({ kind: 'ticket', note: n })),
    // A time entry with neither summary nor internal text still belongs in the timeline —
    // otherwise logged time is invisible here (new entries never carry summaryNotes).
    ...time
      .filter((t) => t.summaryNotes?.trim() || !t.internalNotes?.trim())
      .map((t): NoteEntry => ({ kind: 'time-summary', entry: t })),
    ...time.filter((t) => t.internalNotes?.trim()).map((t): NoteEntry => ({ kind: 'time-internal', entry: t })),
  ];
  // Sort strictly by the actual creation timestamp, regardless of entry type.
  // Time entries' dateWorked is normalised to midnight UTC, so it can't be used for
  // ordering — use createDateTime (the real insert time) and fall back to dateWorked.
  const entryTimestamp = (e: NoteEntry): string =>
    e.kind === 'ticket' ? e.note.createDateTime : (e.entry.createDateTime ?? e.entry.dateWorked);
  allNoteEntries.sort(
    (a, b) => new Date(entryTimestamp(b)).getTime() - new Date(entryTimestamp(a)).getTime(),
  );
  const visibleNoteEntries = allNoteEntries.filter((item) => {
    if (!hideWorkflow) return true;
    if (item.kind !== 'ticket') return true;
    return item.note.creatorResourceID !== 4;
  });

  const totalHours = time.reduce((sum, t) => sum + (Number(t.hoursWorked) || 0), 0);
  const ds = dueState(ticket.dueDateTime);

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-4 md:px-6">
      {/* Breadcrumb / actions */}
      <div className="flex items-center justify-between gap-2">
        <Button variant="ghost" size="sm" onClick={goBack} className="-ml-2.5 text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4"/> Back
        </Button>
        <div className="flex items-center gap-1.5 sm:gap-2">
          {originalEmail && (
            <EmlViewer
              attachmentId={originalEmail.id}
              label="Original Email"
              trigger={
                <Button variant="outline" size="sm" aria-label="Original Email" title="Original Email">
                  <Mail className="h-3.5 w-3.5 sm:mr-1"/>
                  <span className="hidden sm:inline">Original Email</span>
                </Button>
              }
            />
          )}
          <Button asChild variant="outline" size="sm">
            <a href={`${AUTOTASK_TICKET_URL}${id}`} target="_blank" rel="noreferrer" aria-label="Open in Autotask" title="Open in Autotask">
              <ExternalLink className="h-3.5 w-3.5 sm:mr-1"/>
              <span className="hidden sm:inline">Open in Autotask</span>
            </a>
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" aria-label="More">
                <MoreHorizontal className="h-4 w-4"/>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[200px]">
              <DropdownMenuItem
                className="gap-2"
                onSelect={() => {
                  navigator.clipboard.writeText(window.location.href)
                    .then(() => toast.success('Link copied'))
                    .catch(() => toast.error('Could not copy link'));
                }}
              >
                <Link2 className="h-3.5 w-3.5 text-muted-foreground"/> Copy link
              </DropdownMenuItem>
              <DropdownMenuItem
                className="gap-2"
                disabled={!ticket.ticketNumber}
                onSelect={() => {
                  navigator.clipboard.writeText(String(ticket.ticketNumber))
                    .then(() => toast.success('Ticket number copied'))
                    .catch(() => toast.error('Could not copy ticket number'));
                }}
              >
                <Hash className="h-3.5 w-3.5 text-muted-foreground"/> Copy ticket number
              </DropdownMenuItem>
              <DropdownMenuItem
                className="gap-2"
                disabled={detail.isFetching || history.isFetching}
                onSelect={() => {
                  detail.refetch();
                  history.refetch();
                  toast.success('Refreshing…');
                }}
              >
                <RefreshCw className={cn('h-3.5 w-3.5 text-muted-foreground', (detail.isFetching || history.isFetching) && 'animate-spin')}/> Refresh
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Responsive layout — mobile order: header → properties → notes; desktop: two columns */}
      {/* The side rail spans both rows. Left implicit, a rail taller than the two left-hand
          rows combined has its excess split across BOTH of them — which stretched the details
          card to fill the screen whenever the active tab was short (the Time tab). Pinning row 1
          to auto sends all the slack to row 2 instead. */}
      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-[1fr_320px] md:grid-rows-[auto_1fr]">
        {/* Header card */}
        <section className="order-1 min-w-0 rounded-lg border border-border bg-card p-5 shadow-sm md:order-none md:col-start-1 md:row-start-1 md:self-start">
            <div className="flex flex-wrap items-center gap-2">
              {statusLabel   && <StatusBadge kind={statusKind(statusLabel)} dot>{statusLabel}</StatusBadge>}
              {priorityLabel && <PriorityTag priority={priorityLabel}/>}
              {ticket.dueDateTime && ds === 'overdue' && (
                <StatusBadge kind="destructive" dot>Overdue · {format(new Date(ticket.dueDateTime), 'PP')}</StatusBadge>
              )}
              {ticket.dueDateTime && ds === 'soon' && (
                <StatusBadge kind="warning" dot>Due {format(new Date(ticket.dueDateTime), 'PP')}</StatusBadge>
              )}
              <span className="ml-auto font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                {ticket.ticketNumber}
              </span>
            </div>
            <h1 className="mt-2 text-[22px] font-[650] leading-tight tracking-[-0.025em] text-foreground">
              {ticket.title}
            </h1>
            {ticket.companyID != null && (
              <div className="mt-3 flex items-center gap-2.5">
                <CompanyMark name={companyName ?? `#${ticket.companyID}`} size={22}/>
                <OrbitLink companyId={ticket.companyID} className="text-[13.5px] font-medium text-foreground">
                  {companyName ?? `#${ticket.companyID}`}
                </OrbitLink>
                {ticket.createDate && (
                  <>
                    <span className="text-muted-foreground/40">·</span>
                    <span className="text-xs text-muted-foreground">
                      Created {format(new Date(ticket.createDate), 'PP p')}
                    </span>
                  </>
                )}
              </div>
            )}

            {ticket.description && (
              <div className="mt-4 border-t border-divider pt-4 text-sm leading-relaxed text-foreground/80">
                <div className="whitespace-pre-wrap break-words">{renderWithCidImages(ticket.description, attachments)}</div>
              </div>
            )}
          </section>

        {/* Notes / Time / Files */}
        <div className="order-3 min-w-0 space-y-4 md:order-none md:col-start-1 md:row-start-2">
          <Tabs defaultValue="notes">
            <TabsList className="border-b border-border bg-transparent p-0">
              <TabsTrigger value="notes" className="data-[state=active]:border-b-2 data-[state=active]:border-brand">
                Notes <span className="ml-1.5 inline-flex h-4 min-w-[18px] items-center justify-center rounded-full bg-muted px-1.5 text-[10.5px] tabular-nums text-muted-foreground">{allNoteEntries.length}</span>
              </TabsTrigger>
              <TabsTrigger value="time" className="data-[state=active]:border-b-2 data-[state=active]:border-brand">
                Time <span className="ml-1.5 inline-flex h-4 items-center rounded-full bg-muted px-1.5 text-[10.5px] tabular-nums text-muted-foreground">{totalHours.toFixed(2)}h</span>
              </TabsTrigger>
              <TabsTrigger value="attachments" className="data-[state=active]:border-b-2 data-[state=active]:border-brand">
                Files <span className="ml-1.5 inline-flex h-4 min-w-[18px] items-center justify-center rounded-full bg-muted px-1.5 text-[10.5px] tabular-nums text-muted-foreground">{attachments.length}</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="notes" className="mt-4 space-y-3">
              <NoteForm ticketId={id} templateContext={templateContext}/>

              <div className="flex items-center justify-between">
                <div className="text-xs text-muted-foreground">
                  {visibleNoteEntries.length} note{visibleNoteEntries.length !== 1 ? 's' : ''}
                  {hideWorkflow && allNoteEntries.some((item) => item.kind === 'ticket' && item.note.creatorResourceID === 4) && (
                    <span className="ml-1 opacity-60">· workflow notes hidden</span>
                  )}
                </div>
                <label className="flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground select-none">
                  <input
                    type="checkbox"
                    checked={!hideWorkflow}
                    onChange={(e) => {
                      const show = e.target.checked;
                      setHideWorkflow(!show);
                      localStorage.setItem('cosmos:notes:hide-workflow', String(!show));
                    }}
                    className="rounded"
                  />
                  Show workflow notes
                </label>
              </div>

              {visibleNoteEntries.length === 0 ? (
                <EmptyState>No notes yet.</EmptyState>
              ) : (
                visibleNoteEntries.map((item) => {
                  if (item.kind === 'ticket') {
                    const n = item.note;
                    const author = resolveNoteAuthor(n, resourceMap, contactMap);
                    const titleColorClass =
                      n.publish === 2 ? 'text-brand-strong dark:text-brand' :
                      n.publish === 4 ? 'text-success' :
                      'text-muted-foreground';
                    return (
                      <article
                        key={`note-${n.id}`}
                        className={cn(
                          'rounded-md border border-border bg-card px-4 py-3 shadow-sm',
                          n.publish === 2 && 'rail-brand',
                          n.publish === 4 && 'rail-success',
                        )}
                      >
                        <header className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-2.5">
                            <UserAvatar name={author} size={28}/>
                            <div>
                              <div className="text-[13px] font-medium leading-tight text-foreground">{author}</div>
                              <div className="text-[11px] text-muted-foreground">{fmtRelative(n.createDateTime)}</div>
                            </div>
                          </div>
                          <NoteTypeBadge publish={n.publish}/>
                        </header>
                        {n.title && (
                          <div className={cn('mt-2 text-[12px] font-semibold', titleColorClass)}>{n.title}</div>
                        )}
                        <div className="mt-2 whitespace-pre-wrap break-words text-sm leading-relaxed text-foreground/85">
                          {renderWithCidImages(n.description, attachments)}
                        </div>
                      </article>
                    );
                  }

                  // Time entry note (summary or internal)
                  const t = item.entry;
                  const author = resourceMap.get(t.resourceID) ?? 'Unknown';
                  const isInternal = item.kind === 'time-internal';
                  const body = isInternal ? t.internalNotes : t.summaryNotes;
                  return (
                    <article
                      key={`time-${t.id}-${item.kind}`}
                      className={cn(
                        'rounded-md border border-border bg-card px-4 py-3 shadow-sm',
                        isInternal && 'rail-brand',
                      )}
                    >
                      <header className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2.5">
                          <UserAvatar name={author} size={28}/>
                          <div>
                            <div className="text-[13px] font-medium leading-tight text-foreground">{author}</div>
                            <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                              <Clock className="h-3 w-3"/>
                              {t.dateWorked ? format(new Date(t.dateWorked), 'PP') : '—'}
                              <span className="mx-0.5 opacity-50">·</span>
                              {Number(t.hoursWorked).toFixed(2)}h
                            </div>
                          </div>
                        </div>
                        {isInternal
                          ? <StatusBadge kind="brand">Internal</StatusBadge>
                          : <StatusBadge kind="neutral">Time Entry</StatusBadge>
                        }
                      </header>
                      {body && (
                        <div className="mt-2.5 whitespace-pre-wrap break-words text-sm leading-relaxed text-foreground/85">
                          {body}
                        </div>
                      )}
                    </article>
                  );
                })
              )}
            </TabsContent>

            <TabsContent value="time" className="mt-4 space-y-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-muted-foreground">
                  {time.length > 0 && `${time.length} ${time.length === 1 ? 'entry' : 'entries'} · ${totalHours.toFixed(2)}h total`}
                </span>
                <Button size="sm" onClick={() => setLogTimeOpen(true)}>
                  <Timer className="mr-1.5 h-3.5 w-3.5"/> Log Time
                </Button>
              </div>
              {detail.data?.time.error && (
                <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                  Autotask rejected the time-entry query: {detail.data.time.error}
                </div>
              )}
              {/* companyID 0 = Autotask's internal company. It accepts time entry writes but
                  never returns them from the API, so an empty list here means nothing. */}
              {ticket.companyID === 0 && (
                <div className="rounded-md border border-orange-400/50 bg-orange-500/10 px-3 py-2 text-xs text-orange-700 dark:border-orange-500/50 dark:text-orange-300">
                  Internal ticket (no client company). Autotask's API doesn't return time entries
                  for internal-company tickets, so time logged here is only visible in Autotask itself.
                </div>
              )}
              {time.length === 0 ? (
                <EmptyState>No time logged yet.</EmptyState>
              ) : (
                <div className="overflow-x-auto rounded-md border border-border bg-card shadow-sm">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2 text-left">Date</th>
                        <th className="px-3 py-2 text-left">Hours</th>
                        <th className="px-3 py-2 text-left">Details</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-divider">
                      {[...time]
                        .sort((a, b) => new Date(b.dateWorked).getTime() - new Date(a.dateWorked).getTime())
                        .map((t) => (
                          <tr key={t.id}>
                            <td className="px-3 py-2">{t.dateWorked ? format(new Date(t.dateWorked), 'PP') : '—'}</td>
                            <td className="px-3 py-2 font-mono tabular-nums">{Number(t.hoursWorked).toFixed(2)}</td>
                            {/* New entries carry internal notes only (the summary goes out as a
                                customer note); older entries still have summaryNotes. */}
                            <td className="px-3 py-2">{t.internalNotes || t.summaryNotes || ''}</td>
                          </tr>
                        ))}
                    </tbody>
                    <tfoot className="bg-muted/30 text-sm font-medium">
                      <tr>
                        <td className="px-3 py-2 text-[11px] uppercase tracking-wider text-muted-foreground">Total</td>
                        <td className="px-3 py-2 font-mono tabular-nums">{totalHours.toFixed(2)}</td>
                        <td/>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </TabsContent>

            <TabsContent value="attachments" className="mt-4">
              {attachments.length === 0 ? (
                <EmptyState>No attachments.</EmptyState>
              ) : (
                <AttachmentsTab attachments={attachments}/>
              )}
            </TabsContent>
          </Tabs>
        </div>

        {/* ── Properties / side rail ──────────────────────────── */}
        <aside className="order-2 space-y-4 md:order-none md:col-start-2 md:row-start-1 md:row-span-2">
          {/* Live session timer + the only entry point for logging time */}
          <div className="space-y-2 rounded-lg border border-border bg-card px-4 py-2.5 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Timer className="h-3.5 w-3.5 shrink-0" />
                <span>Time on ticket</span>
              </div>
              <span className="font-mono text-sm tabular-nums text-foreground">
                {(() => {
                  const h = Math.floor(elapsedSecs / 3600);
                  const m = Math.floor((elapsedSecs % 3600) / 60);
                  const s = elapsedSecs % 60;
                  if (h > 0) return `${h}h ${String(m).padStart(2,'0')}m ${String(s).padStart(2,'0')}s`;
                  return `${m}m ${String(s).padStart(2,'0')}s`;
                })()}
              </span>
            </div>
            <Button size="sm" className="w-full" onClick={() => setLogTimeOpen(true)}>
              Log Time
            </Button>
          </div>

          <TicketAssistant ticketId={id} />

          <SideCard title="Properties">
            <PropRow label="Assignee">
              <span className="inline-flex items-center gap-1.5">
                {assigneeFullName && <UserAvatar name={assigneeFullName} size={20}/>}
                <span className="flex flex-col">
                  <AssignedResourcePicker ticketId={id} value={ticket.assignedResourceID ?? null}/>
                  {assigneeRoleName && (
                    <span className="ml-2 text-[11px] text-muted-foreground">{assigneeRoleName}</span>
                  )}
                </span>
              </span>
            </PropRow>
            <PropRow label="Secondary">
              <SecondaryResourcesPicker ticketId={id} assignedResourceId={ticket.assignedResourceID ?? null}/>
            </PropRow>
            <PropRow label="Contact">
              <ContactPicker ticketId={id} companyId={ticket.companyID ?? null} value={ticket.contactID ?? null}/>
            </PropRow>
            <PropRow label="CC">
              <AdditionalContactsPicker
                ticketId={id}
                companyId={ticket.companyID ?? null}
                primaryContactId={ticket.contactID ?? null}
              />
            </PropRow>
            <PropRow label="Status">
              <InlineEdit ticketId={id} field="status" value={ticket.status ?? null} options={statusOpts} variant="text"/>
            </PropRow>
            <PropRow label="Priority">
              <InlineEdit ticketId={id} field="priority" value={ticket.priority ?? null} options={priorityOpts} variant="text"/>
            </PropRow>
            <PropRow label="Queue">
              <InlineEdit ticketId={id} field="queueID" value={ticket.queueID ?? null} options={queueOpts} variant="text"/>
            </PropRow>
            <PropRow label="Type">
              <InlineEdit
                ticketId={id}
                field="issueType"
                value={ticket.issueType ?? null}
                options={issueTypeOpts}
                variant="text"
                extraFields={ticket.subIssueType != null ? { subIssueType: null } : undefined}
              />
            </PropRow>
            <PropRow label="Sub-Type">
              {ticket.issueType == null ? (
                <span className="text-sm italic text-muted-foreground">Set Type first</span>
              ) : (
                <InlineEdit
                  ticketId={id}
                  field="subIssueType"
                  value={ticket.subIssueType ?? null}
                  options={subIssueTypeOpts.filter((o) => Number(o.parentValue) === ticket.issueType)}
                  variant="text"
                />
              )}
            </PropRow>
            <PropRow label="Due" valueClass={ds === 'overdue' ? 'text-destructive font-medium' : ''}>
              {ticket.dueDateTime ? format(new Date(ticket.dueDateTime), 'PP') : '—'}
            </PropRow>
            <PropRow label="Last update">{fmtRelative(ticket.lastActivityDate)}</PropRow>
          </SideCard>

          <M365UserCard contactId={ticket.contactID ?? null} />

          <HuntressTicketCard ticketId={ticket.id} title={ticket.title ?? null} />
          <ITGlueTicketCard companyId={ticket.companyID ?? null} ciId={ticket.configurationItemID ?? null} title={ticket.title ?? null} />

          {(() => {
            const historyItems = [...(history.data?.items ?? [])]
              .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
              .slice(0, 10);
            if (historyItems.length === 0) return null;
            return (
              <SideCard title="History">
                <div className="divide-y divide-divider">
                  {historyItems.map((h) => {
                    const author = (h.resourceID === 4 || h.resourceID == null)
                      ? 'Autotask'
                      : (resourceMap.get(h.resourceID) ?? 'System');
                    const detail = h.detail
                      ? h.detail.replace(/\r?\nTime in status:[^\r\n]*/g, '').replace(/\r?\n/g, ' ').trim()
                      : '';
                    return (
                      <div key={`${h.id}-${h.action}`} className="flex items-start gap-2.5 px-4 py-2.5">
                        <UserAvatar name={author} size={22}/>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-baseline gap-1.5 flex-wrap">
                            <span className="text-[12px] font-medium text-foreground">{author}</span>
                          </div>
                          <div className="text-[11px] font-medium text-muted-foreground">{h.action}</div>
                          <div className="text-[10.5px] text-muted-foreground/70">{fmtRelative(h.date)}</div>
                          {detail && (
                            <div className="mt-0.5 text-[11px] leading-snug text-foreground/60 line-clamp-2">{detail}</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </SideCard>
            );
          })()}
        </aside>
      </div>

      <LogTimeDialog
        open={logTimeOpen}
        onOpenChange={setLogTimeOpen}
        ticketId={id}
        ticketNumber={ticket.ticketNumber ?? null}
        ticketTitle={ticket.title ?? null}
        companyName={companyName ?? null}
        contactId={ticket.contactID ?? null}
        companyId={ticket.companyID ?? null}
        startTime={openedAt}
      />
    </div>
  );
}

function SideCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="overflow-hidden rounded-md border border-border bg-card shadow-sm">
      <header className="border-b border-divider bg-muted/40 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </header>
      <div>{children}</div>
    </section>
  );
}

function PropRow({ label, children, valueClass }: { label: string; children: React.ReactNode; valueClass?: string }) {
  return (
    <div className="flex items-start justify-between gap-3 border-t border-divider px-4 py-2.5 first:border-t-0">
      <span className="shrink-0 pt-[2px] text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <div className={cn('text-right text-[13px] text-foreground', valueClass)}>
        {children}
      </div>
    </div>
  );
}

function resolveNoteAuthor(
  n: TicketNote,
  resourceMap: Map<number, string>,
  contactMap: Map<number, string>,
): string {
  if (n.creatorResourceID != null) {
    if (n.creatorResourceID === 4) return 'Autotask';
    return resourceMap.get(n.creatorResourceID) ?? 'Unknown';
  }
  if (n.createdByContactID != null) {
    return contactMap.get(n.createdByContactID) ?? 'Customer';
  }
  return 'Unknown';
}

function NoteTypeBadge({ publish }: { publish: number }) {
  if (publish === 2) return <StatusBadge kind="brand">Internal</StatusBadge>;
  if (publish === 4) return <StatusBadge kind="success">Co-Managed</StatusBadge>;
  return <StatusBadge kind="neutral">External</StatusBadge>;
}

function EmptyState({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-dashed border-border bg-card/40 px-4 py-6 text-center text-sm text-muted-foreground">
      {children}
    </div>
  );
}

const IMAGE_EXT_RE = /\.(png|jpe?g|gif|webp|svg|bmp|avif)(\?|$)/i;
function isImageAttachment(a: { contentType?: string; title?: string; fullPath?: string }): boolean {
  if (a.contentType && a.contentType.toLowerCase().startsWith('image/')) return true;
  if (a.title && IMAGE_EXT_RE.test(a.title)) return true;
  if (a.fullPath && IMAGE_EXT_RE.test(a.fullPath)) return true;
  return false;
}

function isEmailAttachment(a: { contentType?: string; title?: string; fullPath?: string }): boolean {
  if ((a.contentType ?? '').toLowerCase() === 'message/rfc822') return true;
  return /\.eml(\?|$)/i.test(a.fullPath ?? a.title ?? '');
}

function AttachmentsTab({ attachments }: { attachments: { id: number; title?: string; fullPath?: string; contentType?: string }[] }) {
  const images = attachments.filter(isImageAttachment);
  const others = attachments.filter((a) => !isImageAttachment(a));

  return (
    <div className="space-y-4">
      {images.length > 0 && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {images.map((a) => {
            const src = `/api/attachments/${a.id}/data`;
            return (
              <a
                key={a.id}
                href={src}
                target="_blank"
                rel="noreferrer"
                className="group block overflow-hidden rounded-md border border-border bg-card shadow-sm transition-shadow hover:shadow-md"
                title={a.title}
              >
                <img src={src} alt={a.title ?? `Attachment ${a.id}`} loading="lazy"
                     className="block max-h-72 w-full bg-muted/30 object-contain"/>
                <div className="truncate border-t border-divider px-2 py-1 text-xs text-muted-foreground group-hover:text-foreground">
                  {a.title ?? `Attachment #${a.id}`}
                </div>
              </a>
            );
          })}
        </div>
      )}

      {others.length > 0 && (
        <ul className="space-y-2">
          {others.map((a) => {
            const isEmail = isEmailAttachment(a);
            const label = a.title ?? `Attachment #${a.id}`;
            return (
              <li key={a.id} className="flex items-center gap-3 rounded-md border border-border bg-card px-3 py-2.5 shadow-sm">
                <span className="grid h-8 w-8 place-items-center rounded-md bg-muted text-muted-foreground">
                  {isEmail ? <Mail className="h-3.5 w-3.5"/> : <Paperclip className="h-3.5 w-3.5"/>}
                </span>
                {isEmail ? (
                  <>
                    <EmlViewer attachmentId={a.id} label={label}/>
                    <a href={`/api/attachments/${a.id}/data?download=1`}
                       className="shrink-0 text-muted-foreground hover:text-foreground" title="Download .eml" aria-label="Download .eml">
                      <Download className="h-4 w-4"/>
                    </a>
                  </>
                ) : (
                  <a href={`/api/attachments/${a.id}/data?download=1`}
                     className="flex-1 truncate text-sm font-medium text-brand-strong hover:underline dark:text-brand">
                    {label}
                  </a>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
